import type { OnboardingState } from './OnboardingState';
import { groupOperatorsByAccounts, Status } from '@/helpers/operatorHelpers';
import {
  EOnbordingStep,
  EOnbordingStepStatus,
  OperatorOnboardingForm,
  TOnboardingProgress,
  IAccountListItem,
  ECopyStakeIntegrationMode,
  ECopyStakeCopyMode,
  ECopyStakeIntegrationType
} from '@/api/schema';

const { COMPLETED } = EOnbordingStepStatus;
const onboardingTestSteps = Object.freeze([
  EOnbordingStep.tokenInfoProgress,
  EOnbordingStep.uiSettingsProgress,
  EOnbordingStep.inviteDevProgress,
  EOnbordingStep.whitelistProgress,
  EOnbordingStep.accountCreationProgress,
  EOnbordingStep.getUrlProgress,
  EOnbordingStep.tokenExchangeProgress,
  EOnbordingStep.trafficProgress
]);

export const checkIsTestComplete = (progress: TOnboardingProgress): boolean => {
  if (Array.isArray(progress)) return false;
  const isCompleted = (s: EOnbordingStep) => progress[s] === COMPLETED;

  return onboardingTestSteps.every(isCompleted);
};

export default {
  /**
   * Get operator env type.
   * @param state Onboarding State.
   * @returns is test env.
   */
  isTestEnv(state: OnboardingState): boolean {
    return !!state.selectedOperator?.test;
  },

  /**
   * Get operator data need for creation.
   * @param state Onboarding State.
   * @returns creation operator data.
   */
  createOperatorData(state: OnboardingState): OperatorOnboardingForm {
    return {
      ...state.onboardingData,
      ...state.newOperator
    };
  },

  /**
   * Get operator onboarding form
   * @param state onboarding state
   * @return onboarding form
   */

  onboardingForm(state: OnboardingState): OperatorOnboardingForm {
    const {
      ggr,
      name,
      test,
      tokenName,
      tokenNumber,
      tokenPrice,
      tokenTicker,
      url
    } = state.onboardingData;

    return {
      ggr,
      name,
      test,
      tokenName,
      tokenNumber,
      tokenPrice,
      tokenTicker,
      url
    };
  },

  /**
   * Get operator id.
   * @param state Onboarding State.
   * @param _
   * @param rootState
   * @return operator id.
   */
  operatorId(
    state: OnboardingState,
    _: unknown,
    rootState: {
      Auth: { operatorId: any; token: string };
      SuperAdmin: { operatorId: any };
    }
  ): number | null {
    if (!rootState.Auth.token) {
      return null;
    }

    return (
      state.selectedOperator?.id || rootState.SuperAdmin.operatorId || null
    );
  },

  /**
   * Get operator id.
   * @param state Onboarding State.
   * @returns test env.
   */
  envName(state: OnboardingState): string | null {
    return (
      state.selectedOperator.name || state.onboardingData.name || 'Operator'
    );
  },

  /**
   * Get current brand name.
   * @param state Onboarding State.
   * @returns current brand name.
   */
  currentBrandName(state: OnboardingState): string {
    return state.selectedOperator.brandName || 'Other';
  },

  /**
   * Get is operator approved by SuperAdmin.
   * @param state Onboarding State.
   * @returns operator approve status.
   */
  operatorApproved(state: OnboardingState): boolean {
    const { status, test } = state.selectedOperator?.id
      ? state.selectedOperator
      : state.onboardingData;

    return (
      status && !(!test && [Status.declined, Status.created].includes(status))
    );
  },

  /**
   * Show mobile app status
   */

  isMobile(state: OnboardingState): boolean {
    return state.accountInfo?.businessType === 'MOBILE_APP';
  },

  /**
   * Show email verification status
   */
  isEmailVerified(state: OnboardingState): boolean {
    return !!state.accountInfo?.emailVerified;
  },

  operatorProgress(state: OnboardingState): Record<string, boolean> {
    const currentId = state.selectedOperator.id;
    const currentData = state.onboardingProgress[currentId];
    if (!currentData) return {};

    return Object.entries(currentData).reduce(
      (all, [key, val]) => ({
        ...all,
        [key]: val === COMPLETED
      }),
      {}
    );
  },

  successOnboardingTestProgress(state: OnboardingState): boolean {
    if (!state.selectedOperator.test) return false;
    const { id } = state.selectedOperator;
    const { [id]: progress } = state.onboardingProgress;

    return progress && checkIsTestComplete(progress);
  },

  operatorListByGroup(
    state: OnboardingState,
    _: { [key: string]: any },
    rootState: { [key: string]: any },
    rootGetters: { [key: string]: any }
  ): IAccountListItem[] {
    const operators = rootState.app.isSuperAdmin
      ? rootGetters['SuperAdmin/operators']
      : state.operators;

    return groupOperatorsByAccounts(operators);
  },

  /**
   * Get current account.
   * @param _ Onboarding State.
   * @param getters getters
   * @returns test env.
   */
  currentAccount(
    _: OnboardingState,
    getters: {
      currentBrandName: string;
      operatorListByGroup: IAccountListItem[];
    }
  ): IAccountListItem {
    return getters.operatorListByGroup.find(
      (account: IAccountListItem) =>
        account.name.toLowerCase() === getters.currentBrandName.toLowerCase()
    );
  },

  /**
   * Get is operator Copystake NoCode integration enabled
   */
  isCopyStakeGeneralIntegrationMode(state: OnboardingState): boolean {
    return (
      state.copyStakeIntegration.integrationMode ===
      ECopyStakeIntegrationMode.GENERAL
    );
  },

  /**
   * Get is operator Copystake NoCode integration enabled
   */
  isCopyStakeNoCodeIntegrationMode(state: OnboardingState): boolean {
    return (
      state.copyStakeIntegration.integrationMode ===
      ECopyStakeIntegrationMode.NOCODE
    );
  },

  /**
   * Get is operator Copystake OnFly copy mode enabled
   */
  isCopyStakeOnFlyCopyMode(
    state: OnboardingState,
    getters: {
      isCopyStakeGeneralIntegrationMode: boolean;
    }
  ): boolean {
    return (
      getters.isCopyStakeGeneralIntegrationMode &&
      state.copyStakeIntegration.copyMode === ECopyStakeCopyMode.ON_FLY
    );
  },

  /**
   * Get is operator Copystake InPool copy mode enabled
   */
  isCopyStakeInPoolCopyMode(
    state: OnboardingState,
    getters: {
      isCopyStakeGeneralIntegrationMode: boolean;
    }
  ): boolean {
    return (
      getters.isCopyStakeGeneralIntegrationMode &&
      state.copyStakeIntegration.copyMode === ECopyStakeCopyMode.IN_POOL
    );
  },

  /**
   * Get is operator Copystake Underground Casino games integration type enabled
   */
  isUndergroundCasinoGamesIntegrationType(state: OnboardingState): boolean {
    return (
      state.copyStakeIntegration.gameIntegrationType ===
      ECopyStakeIntegrationType.UNDERGROUND_CASINO
    );
  },

  /**
   * Get operator Copystake app name
   */
  copyStakeAppName(state: OnboardingState): string {
    return (
      state.copyStakeIntegration.brandName || 'unknown' // Default to 'unknown' if brandName is not set
    );
  }
};
